import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { convertSecondsToTimeParts } from '@lgg/isomorphic/i18n/date-utils';

type UnitsToBeUsedCount = 1 | 2 | 3 | 4;

export const useTimeHelpers = () => {
  const { t } = useTranslation(['common']);

  const convertSecondsToDuration = useCallback(
    (valueInSeconds: number) => {
      const minutes = Math.floor(valueInSeconds / 60).toString();
      const seconds = (valueInSeconds % 60).toString();

      const padStartValue = (value: string) => value.padStart(2, '0');

      const durationLabel =
        valueInSeconds >= 60
          ? t('common:timeUnits.minute_short')
          : t('common:timeUnits.second_short');

      return `${padStartValue(minutes)}:${padStartValue(seconds)} ${durationLabel}`;
    },
    [t],
  );

  const getAbbreviateComposedDurationRelevantUnits = useCallback(
    (inputInSeconds: number, unitsToBeUsed: UnitsToBeUsedCount = 2) => {
      const { days, hours, minutes, seconds } = convertSecondsToTimeParts(inputInSeconds);
      const result = {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      };
      let unitsUsed = 0;

      if (days) {
        result.days = days;
        unitsUsed++;

        if (days >= 100) {
          return result;
        }
      }

      if (hours && unitsUsed < unitsToBeUsed) {
        result.hours = hours;
        unitsUsed++;
      }

      if (minutes && unitsUsed < unitsToBeUsed) {
        result.minutes = minutes;
        unitsUsed++;
      }

      if (seconds && unitsUsed < unitsToBeUsed) {
        result.seconds = seconds;
      }

      return result;
    },
    [],
  );

  const formatAbbreviatedComposedDuration = useCallback(
    ({
      inputInSeconds,
      unitsToBeUsed = 2,
      defaultValue = '',
    }: {
      inputInSeconds: number;
      unitsToBeUsed?: UnitsToBeUsedCount;
      defaultValue?: string;
    }) => {
      const { days, hours, minutes, seconds } =
        getAbbreviateComposedDurationRelevantUnits(inputInSeconds, unitsToBeUsed);

      let result = '';

      const addValueToResult = (value: string) => {
        result += result.length > 0 ? ` ${value}` : value;
      };

      if (days) {
        addValueToResult(
          t('common:timeUnits.day_short_short', { value: days.toLocaleString() }),
        );
      }

      if (hours) {
        addValueToResult(
          t('common:timeUnits.hour_short_short', { value: hours.toLocaleString() }),
        );
      }

      if (minutes) {
        addValueToResult(
          t('common:timeUnits.minute_short_short', { value: minutes.toLocaleString() }),
        );
      }

      if (seconds) {
        addValueToResult(
          t('common:timeUnits.second_short_short', { value: seconds.toLocaleString() }),
        );
      }

      return result.length > 0 ? result : defaultValue;
    },
    [getAbbreviateComposedDurationRelevantUnits, t],
  );

  return {
    convertSecondsToDuration,
    formatAbbreviatedComposedDuration,
    getAbbreviateComposedDurationRelevantUnits,
  };
};
